// Peek (blueprint #26): a quick look at a source WITHOUT diving in - no trail
// crumb, no depth change, never marked explored. Satisfies curiosity without
// derailing the journey. One cached getText (reuses the per-session textCache, so
// a later real Open is free), single-flighted so a stale response can never paint
// into a sheet the user already replaced or closed. Mobile = bottom-sheet; the
// same component works as a desktop popover via CSS. Everything sanitized; names
// and labels go through textContent only - the he/en body is rendered by the shared
// textBlock (the one sanitizer chokepoint), never a label. prefers-reduced-motion in CSS.

import { el, button } from "../lib/dom.ts";
import { getText } from "../lib/sefaria.ts";
import { trapFocus, type TrapHandle } from "../lib/focusTrap.ts";
import { skeletonLines } from "./skeleton.ts";
import { textBlock, errorRow } from "./textBlock.ts";
import { syncOverlayBackground } from "../lib/overlayBackground.ts";

export interface PeekOpts {
  ref: string;
  label: string; // English display label (textContent only)
  labelHe: string | null; // Hebrew label (textContent only)
  relationshipLabel: string; // honest, anchor-agnostic (textContent only)
  colorVar: string; // source-type ink, e.g. "--t-commentary"
  // Promote the peek into a real Open in the journey. OPTIONAL: a ref that has
  // no card in the current citelist (a text mention resolved by find-refs) is
  // peek-only - there is nothing to promote into, so no button renders.
  onOpenHere?: () => void;
  onBookmark?: (btn: HTMLButtonElement) => void;
  bookmarked?: boolean;
}

// Single-flight guard: only the most-recently-opened ref may paint its result.
let activeRef: string | null = null;
let openTrap: TrapHandle | null = null;
let lastFocus: HTMLElement | null = null;
let currentScrim: HTMLElement | null = null;
let currentSheet: HTMLElement | null = null;

function teardown(scrim: HTMLElement, sheet: HTMLElement): void {
  openTrap?.release();
  openTrap = null;
  scrim.remove();
  sheet.remove();
  currentScrim = null;
  currentSheet = null;
  activeRef = null;
  syncOverlayBackground(); // un-inert the background BEFORE returning focus to it
  lastFocus?.focus?.();
  lastFocus = null;
}

export function openPeek(opts: PeekOpts): void {
  // Only one peek at a time: tear down any open sheet (and release its trap) before
  // opening a new one, so peeks never stack and a prior keydown listener can't leak.
  if (currentScrim && currentSheet) teardown(currentScrim, currentSheet);
  activeRef = opts.ref;
  lastFocus = (document.activeElement as HTMLElement) ?? null;

  const scrim = el("div", "peek-scrim");
  const sheet = el("div", "peek-sheet");
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  sheet.setAttribute("aria-label", `Peek at ${opts.label}`);

  const grip = el("div", "drawer-grip");
  grip.setAttribute("aria-hidden", "true");

  const head = el("div", "peek-head");
  const dot = el("span", "ec-dot peek-dot");
  dot.style.setProperty("--dot", `var(${opts.colorVar})`);
  dot.setAttribute("aria-hidden", "true");
  const heads = el("div", "peek-heads");
  if (opts.labelHe) {
    const he = el("span", "peek-he", opts.labelHe);
    he.lang = "he";
    he.dir = "rtl";
    heads.append(he);
  }
  heads.append(el("span", "peek-name", opts.label));
  const rel = el("span", "peek-rel", `${opts.relationshipLabel} · peek, not opened`);
  heads.append(rel);
  head.append(dot, heads);

  const body = el("div", "peek-body");
  body.append(skeletonLines(3));

  const foot = el("div", "peek-foot");
  const onOpenHere = opts.onOpenHere;
  if (onOpenHere) {
    const openBtn = button("btn btn-sm", "Open here", () => {
      teardown(scrim, sheet);
      onOpenHere();
    });
    openBtn.setAttribute("aria-label", `Open ${opts.label} in your journey`);
    foot.append(openBtn);
  }
  if (opts.onBookmark) {
    const mark = (b: HTMLButtonElement): void => {
      b.textContent = "Bookmarked";
      b.classList.add("kept");
      b.setAttribute("aria-label", `${opts.label} is bookmarked`);
      b.title = `${opts.label} is bookmarked`;
    };
    const bookmarkBtn = button("btn btn-sm peek-bookmark", opts.bookmarked ? "Bookmarked" : "Bookmark", () => {
      opts.onBookmark?.(bookmarkBtn);
    });
    bookmarkBtn.setAttribute("aria-label", `Bookmark ${opts.label}`);
    bookmarkBtn.title = `Bookmark ${opts.label}`;
    if (opts.bookmarked) mark(bookmarkBtn);
    foot.append(bookmarkBtn);
  }
  const closeBtn = button("peek-close", "Close", () => teardown(scrim, sheet));
  foot.append(closeBtn);

  sheet.append(grip, head, body, foot);
  scrim.addEventListener("click", () => teardown(scrim, sheet));
  document.body.append(scrim, sheet);
  currentScrim = scrim;
  currentSheet = sheet;
  syncOverlayBackground(); // inert the app behind the peek sheet

  openTrap = trapFocus(sheet, {
    initialFocus: closeBtn,
    onEscape: () => teardown(scrim, sheet),
  });

  const myRef = opts.ref;
  void getText(myRef)
    .then((t) => {
      if (activeRef !== myRef) return; // a newer peek superseded this one - drop the stale paint
      body.replaceChildren(textBlock(t.he, t.en));
    })
    .catch((err) => {
      if (activeRef !== myRef) return;
      body.replaceChildren(errorRow(err));
    });
}

